import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import EmailLookupWrapper from 'tio-employee/components/auth/email-lookup-wrapper';
import emailSchema from 'tio-common/validation-schema/models/email';
import { sendForgotPasswordRequest } from 'tio-common/requests/login';
import Input from 'tio-reimagined/components/input';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { safeParse } from 'valibot';

export default class ForgotPasswordRoute extends Component<{ Args: {} }> {
  @service declare store: StoreService;
  @service declare router: RouterService;

  @tracked email = '';
  @tracked isLoading = false;

  constructor(owner: Owner, args: {}) {
    super(owner, args);
    this.email = this.emailFromQueryParams;
  }

  get validationErrors() {
    const result = safeParse(emailSchema, { email: this.email });
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get emailFromQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return decodeURIComponent(urlParams.get('email') || '');
  }

  sendPasswordEmail = dropTask(async () => {
    try {
      await this.store.request(sendForgotPasswordRequest(this.email));
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading = false;
      this.router.transitionTo('forgot-password.confirmation');
    }
  });

  get isDisabled() {
    return !this.email || !!this.validationErrors.length;
  }

  submitForgotPassword = async () => {
    this.isLoading = true;
    if (this.validationErrors.length) return;
    this.sendPasswordEmail.perform();
  };

  backToLogin = () => {
    this.router.transitionTo('login');
  };

  <template>
    <div class="flex flex-col h-screen container mx-auto w-full overflow-auto">
      <EmailLookupWrapper @route="forgot-password">
        <h1 class="text-xl my-4 text-center">{{t "login.forgot_password_question"}}</h1>
        <p class="mb-4">{{t "login.forgot_password_body"}}</p>
        <form {{on "submit" this.submitForgotPassword}} data-test-forgot-password-form>
          <div class="mb-10">
            <Input
              @label={{t "login.email"}}
              @value={{this.email}}
              @onChange={{fn (mut this.email)}}
              @errors={{this.validationErrors}}
              @type="email"
              data-test-email-field
            />
          </div>
          <div class="tio-row-center gap-x-4 mb-10">
            <Button @appearance="outlined" {{on "click" this.backToLogin}} data-test-back-button>
              {{t "login.back_to_login"}}
            </Button>
            <Button
              @intent="primary"
              {{on "click" this.submitForgotPassword}}
              @isDisabled={{this.isDisabled}}
              data-test-submit-button
            >
              {{t "common.submit"}}
            </Button>
          </div>
        </form>
      </EmailLookupWrapper>
    </div>
  </template>
}
